// Australian wording for the /au/ site as [US text, AU text] pairs. The build
// applies them to AU page text (scripts/build-au.mjs) and w() applies them to
// app messages at runtime. Matching is case-sensitive on whole words, longest
// phrase first. First-pass draft, pending review by an Australian contact.
//
// Input-field unit labels ("Height (feet)") are deliberately absent: they
// change together with the inputs themselves, or users would type metres into
// a field still read as feet.

export const AU_WORDING = [
    // Page titles and descriptions (Australians search "caravan", not "RV")
    ['Zephyr - Smart RV Navigation', 'Zephyr - Smart Caravan & Motorhome Navigation'],
    ['Zephyr — Smarter RV Trip Planning', 'Zephyr — Smarter Caravan & Motorhome Trip Planning'],
    ['Plan safer, more comfortable routes for your RV travels.', 'Plan safer, more comfortable routes for your caravan or motorhome.'],
    ['Built by full-time RVers, for RVers.', 'Built by full-time RVers, for caravanners and motorhomers.'],

    // Founders' story: true US events, so keep them and explain the US names
    ["AI told us there was a Love's in rural Texas.", "AI told us there was a Love's truck stop in rural Texas."],
    ['A 40-foot travel trailer in a 35mph crosswind', 'A 12-metre caravan in a 56 km/h crosswind'],

    // Who it's for
    ['Zephyr is built by full-timers, for everyone who drives an RV', 'Zephyr is built by full-timers, for everyone who tows a caravan or drives a motorhome'],
    ['Built by full-timers, for everyone who drives an RV', 'Built by full-timers, for everyone who tows a caravan or drives a motorhome'],
    ['Features Built for RV Travelers', 'Features Built for Caravanners and Motorhomers'],
    ['the tools built for RV travelers', 'the tools built for caravanners and motorhomers'],
    ['Smart navigation for RV travelers', 'Smart navigation for caravanners and motorhomers'],
    ['Plan your next RV trip', 'Plan your next caravan or motorhome trip'],
    ['Start planning safer, more comfortable RV routes today', 'Start planning safer, more comfortable caravan and motorhome routes today'],
    ['every RV traveler', 'every traveller'],
    ["this RV traveler's journey", "this traveller's journey"],
    ['other RV travelers', 'other travellers'],
    ['Help other RVers', 'Help other travellers'],
    ['helpful for other RVers', 'helpful for other travellers'],
    ['accuracy for other RVers', 'accuracy for other travellers'],
    ['reported site conditions from other RVers', 'reported site conditions from other travellers'],

    // Vehicle types: labels only, the stored values (class-a, travel-trailer...) are unchanged
    ['Class A Motorhome (Bus-style)', 'Large Motorhome (Bus-style)'],
    ['Class B Motorhome (Van)', 'Campervan'],
    ['Class C Motorhome (Cab-over)', 'Motorhome (Cab-over)'],
    ['Class A, Class C, fifth wheel, travel trailer, or truck camper', 'large motorhome, motorhome, campervan, fifth wheeler, caravan or slide-on camper'],
    ['(travel trailer, Class A, etc.)', '(caravan, motorhome, etc.)'],
    ['Class A Motorhome', 'Large Motorhome'],
    ['Class B Motorhome', 'Campervan'],
    ['Class B Van', 'Campervan'],
    ['Class C Motorhome', 'Motorhome'],
    ['Fifth Wheel Trailer', 'Fifth Wheeler'],
    ['Travel Trailer/Fifth Wheel', 'Caravan/Fifth Wheeler'],
    ['Travel Trailers', 'Caravans'],
    ['Travel Trailer', 'Caravan'],
    ['travel trailers', 'caravans'],
    ['travel trailer', 'caravan'],
    ['Fifth Wheel', 'Fifth Wheeler'],
    ['fifth wheel', 'fifth wheeler'],
    ['truck camper', 'slide-on camper'],

    // Tow vehicles: mapped by towing class to the closest Australian vehicles
    ['1/2 Ton Truck (F-150, Silverado 1500, Ram 1500)', 'Ute or Large 4WD (Ranger, HiLux, LandCruiser, Patrol)'],
    ['3/4 Ton Truck (F-250, Silverado 2500, Ram 2500)', 'Full-size Pickup (RAM 1500, Silverado 1500, F-150)'],
    ['1 Ton Truck (F-350, Silverado 3500, Ram 3500)', 'Heavy-duty Pickup (RAM 2500/3500, Silverado HD, F-250)'],
    ['Mid-size SUV/Truck (4Runner, Tacoma)', 'Mid-size SUV (Prado, Everest, Pajero Sport)'],
    ['Turbocharged Gas', 'Turbo Petrol'],
    ['Gas V6', 'Petrol V6'],
    ['Gas V8', 'Petrol V8'],
    ['Gas', 'Petrol'],

    // FAQ clearance margins (the app's own metric margins: ClearanceService)
    ['less than 6 inches to spare', 'less than 15 cm to spare'],
    ['less than 12 inches to spare', 'less than 30 cm to spare'],

    // Fuel
    ['All Gas Stations (Recommended)', 'All Fuel Stations (Recommended)'],
    ['Show Gas Stations', 'Show Fuel Stations'],
    ['Gas Stations', 'Fuel Stations'],
    ['Gas stations', 'Fuel stations'],
    ['gas stations', 'fuel stations'],
    ['Gas Station', 'Fuel Station'],
    ['Community-confirmed truck stops and stations', 'Community-confirmed truck stops and roadhouses'],
    ['dump stations', 'dump points'],
    ['RV Dump', 'Dump Point'],
    ['propane', 'LPG'],
    ['Propane', 'LPG'],

    // Campgrounds
    ['Pine Valley RV Park', 'Pine Valley Caravan Park'],
    ['RV parks and campgrounds within', 'Caravan parks and campgrounds within'],
    ['RV parks and campgrounds', 'caravan parks and campgrounds'],
    ['RV Park', 'Caravan Park'],
    ['State Park', 'National Park'],
    ['Filter by hookups, rig size, amenities, and more.', 'Filter by powered sites, amenities, and more.'],
    ['to filter by hookups, rig size, amenities', 'to filter by powered sites and amenities'],
    ['Filter by hookups', 'Filter by powered sites'],
    ['to filter by hookups', 'to filter by powered sites'],
    ['Full Hookups', 'Powered sites'],
    ['Pull-Through', 'Drive-Through'],
    ['Pull-Thru', 'Drive-Thru'],
    ['Dump Station', 'Dump Point'],
    ['pull-through access', 'drive-through access'],
    ['Pull-through access', 'Drive-through access'],
    ['Pull-through access for RVs?', 'Drive-through bay long enough for a caravan?'],
    ['RV-Friendly Only (truck stops + traveler-rated)', 'Caravan-friendly only (traveller-rated)'],
    ['✓ RV-Friendly — confirmed by', '✓ Caravan-friendly — confirmed by'],
    ['report pull-through access', 'report a drive-through bay'],
    ['✓ Rate this stop for RVs', '✓ Rate this stop for caravans'],
    ['Green markers (🚛) = RV-friendly', 'Green markers (🚛) = caravan-friendly'],

    // Pro is sold in Australia only through Google Play, at the A$ price set there
    ['Pro — $5.99/mo', 'Pro — A$9.99/mo'],
    ['$5.99/month', 'A$9.99/month'],

    // Wind-safety sentences whose numbers depend on each other
    ['For every foot of height above 10 feet, thresholds are reduced by 3%.', 'For every 0.3 m of height above 3.0 m, thresholds are reduced by 3%.'],
    ['A 13-ft travel trailer has Caution at 23 mph and Danger at 28 mph (vs. 25/30 for 10-ft).', 'A 4.0 m caravan has Caution at 37 km/h and Danger at 45 km/h (vs. 40/48 for 3.0 m).'],
    ['4,000 lbs (80% of 5,000)', '1,810 kg (80% of 2,270 kg)'],
    ['28 ft (112% of 25)', '8.5 m (112% of 7.6 m)'],
    ['could overturn an 18-ft travel trailer', 'could overturn a 5.5 m caravan'],

    // Australian spelling
    ['Favorites', 'Favourites'],
    ['favorites', 'favourites'],
    ['Favorite', 'Favourite'],
    ['favorite', 'favourite'],
    ['Customize', 'Customise'],
    ['customize', 'customise'],
    ['personalizes', 'personalises'],
    ['Personalized', 'Personalised'],
    ['personalize', 'personalise'],
    ['analyzes', 'analyses'],
    ['anonymized', 'anonymised'],
    ['optimized', 'optimised'],
    ['center of gravity', 'centre of gravity'],
    ['Color-Coded', 'Colour-Coded'],
    ['color-codes', 'colour-codes'],
    ['Travelers', 'Travellers'],
    ['travelers', 'travellers'],
    ['Traveler', 'Traveller'],
    ['traveler', 'traveller'],
    ['traveling', 'travelling'],
    // Long Hauler badge: 1,500 km for Australian accounts (lambda/routes awards it there)
    ['Planned a trip over 1,000 miles', 'Planned a trip over 1,500 km'],
    ['planned a 1,000+ mile RV trip', 'planned a 1,500+ km trip'],
    ['tire', 'tyre'],
    ['tires', 'tyres']
];

import { speed, distance, length, weight } from '../services/units.js';

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const edge = (ch, side) => (/\w/.test(ch) ? (side === 'start' ? '(?<![\\w-])' : '(?![\\w-])') : '');

const RULES = [...AU_WORDING]
    .sort((a, b) => b[0].length - a[0].length)
    .map(([us, au]) => [new RegExp(edge(us[0], 'start') + escape(us) + edge(us[us.length - 1], 'end'), 'g'), au]);

// One pass with all phrases, so a replacement is never rewritten by a shorter rule.
const COMBINED = new RegExp(RULES.map(([re]) => `(${re.source})`).join('|'), 'g');

export function applyAuWording(text) {
    return text.replace(COMBINED, (...m) => {
        const i = m.slice(1, RULES.length + 1).findIndex((g) => g !== undefined);
        return RULES[i][1];
    });
}

const num = (s) => Number(s.replace(/,/g, ''));
const oneDecimal = (n) => n.toFixed(1);

// Imperial measurements written into page text ("35 mph", "40-foot") in metric.
// Converts with the current unit system, so call it only when that is metric.
export function metricText(text) {
    return text
        .replace(/(\d+)\s?[-–]\s?(\d+)\s?mph\b/g, (_, a, b) => `${speed(num(a))}–${speed(num(b))} km/h`)
        .replace(/(\d[\d,]*(?:\.\d+)?)\s?mph\b/g, (_, n) => `${speed(num(n))} km/h`)
        .replace(/(\d[\d,]*(?:\.\d+)?)\s?(?:miles|mile)\b/g, (_, n) => `${distance(num(n))} km`)
        .replace(/(\d[\d,]*(?:\.\d+)?)(?:\s|-)?(?:feet|foot|ft)\b/g, (_, n) => `${oneDecimal(length(num(n)))} m`)
        .replace(/(\d[\d,]*(?:\.\d+)?)\s?(?:lbs|lb)\b/g, (_, n) => `${weight(num(n)).toLocaleString('en-AU')} kg`);
}
